/**
 * DialogueWorkspace — dual-dialogue view для email и telegram.
 *
 * Роутит InboxItem в EmailDialogue или TelegramDialogue,
 * каждый из которых рендерит Shadow + Real panels.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConversationSummary } from './ConversationSummary';
import { inboxIdToSource } from '../../lib/inbox-id';
import {
  ShadowPanelView,
  RealPanelView,
  type Message,
  type Proposal,
  type ProposalReviewActions,
  type ProposalActionError,
} from '../chat';
import { getErrorMessage } from '../../lib/error-messages';
import {
  useDialogueProposals,
  type ProposalItem,
  useGenerateProposal,
  useAcceptProposal,
  useRejectProposal,
  useEditProposal,
  type InboxItem,
} from '../../api/inbox';
import { useThread, useSendReply, useRetryMessage } from '../../api/threads';
import {
  useTelegramMessages,
  useTelegramProposals,
  useSendTelegramMessage,
  type TelegramMessage,
  type TelegramProposal,
} from '../../api/telegram';
import { useWidgetMessages, useSendWidgetReply, type WidgetMessage } from '../../api/widget';
import {
  usePromoteKnowledge,
  useCreateCheckpoint,
  useSetChatMode,
  useDialogueModes,
  type GenerationMode,
} from '../../api/auto-reply';

// ============================================================================
// Transformers
// ============================================================================

export function telegramMsgToMessage(msg: TelegramMessage): Message {
  return {
    id: msg.id,
    text: msg.text,
    isOutbound: msg.isOutbound,
    senderName: msg.fromUsername,
    senderRole: msg.isOutbound ? 'operator' : 'client',
    timestamp: msg.receivedAt,
  };
}

/**
 * Черновик из общего ответа API — для почты и виджета.
 *
 * ОДНА функция, а не литерал в каждом месте. До 10.09 их было два, и оба
 * теряли поля: почта роняла `skipReason` и `observerSignals`, виджет —
 * `skipReason`. То есть причина «черновика нет» доходила до оператора ровно в
 * одном канале из трёх, хотя API её отдавал. Третья копия для телеграма живёт
 * ниже отдельно, потому что у него свой тип ответа.
 */
function элементВЧерновик(p: ProposalItem): Proposal {
  return {
    id: p.id,
    text: p.text,
    originalText: p.originalText,
    status: p.status,
    matchScore: p.matchScore,
    matchCategory: p.matchCategory as Proposal['matchCategory'],
    generationContext: p.generationContext as Proposal['generationContext'],
    observerSignals: p.observerSignals ?? null,
    shadowDecision: p.shadowDecision,
    skipReason: p.skipReason,
    createdAt: p.createdAt,
  };
}

export function telegramProposalToProposal(p: TelegramProposal): Proposal {
  return {
    id: p.id,
    text: p.text,
    originalText: p.originalText,
    status: p.status,
    matchScore: p.matchScore,
    matchCategory: p.matchCategory,
    generationContext: p.generationContext,
    observerSignals: p.observerSignals,
    shadowDecision: p.shadowDecision,
    skipReason: p.skipReason,
    createdAt: p.createdAt,
  };
}

/**
 * Почему черновик не готовится — словами для оператора.
 *
 * Одна функция на три канала: текст отказа — это знание, и три копии
 * разъехались бы, а на четвёртом канале про неё просто забыли бы. Без такого
 * перевода оператор видит либо техническую строку, либо ничего — второе и
 * есть тихий отказ, из-за которого эта работа началась.
 */
function ошибкаПодготовки(error: unknown): string | null {
  if (!error) return null;
  const ответ = (error as { response?: { status?: number; data?: { error?: string } } }).response;
  if (ответ?.data?.error) {
    // Сервер уже отвечает по-русски: 409 «уже готовится», 404 «отвечать
    // пока нечего», 503 «не удалось поставить в очередь»
    return ответ.data.error;
  }
  return 'Не удалось подготовить черновик — попробуйте ещё раз';
}

// ============================================================================
// Review actions hook
// ============================================================================

function useReviewActions(sourceKey: string): ProposalReviewActions {
  const { mutate: acceptMutate, isPending: isAccepting } = useAcceptProposal();
  const { mutate: rejectMutate } = useRejectProposal();
  const { mutate: editMutate } = useEditProposal();

  // Отказ отправки обязан быть виден на том же черновике, по которому
  // нажали. До 06.09 ошибка принятия не показывалась нигде: маршрут мог
  // ответить отказом Telegram или «черновик уже принят», а оператор видел
  // ровно то же, что при успехе — ничего
  const [acceptError, setAcceptError] = useState<ProposalActionError | null>(null);

  const onAccept = useCallback((proposalId: string) => {
    setAcceptError(null);
    acceptMutate(proposalId, {
      onError: (error) => {
        // 409 означает, что черновик уже забрали — почти всегда это второй
        // щелчок по той же кнопке, то есть ответ как раз УШЁЛ. Показать здесь
        // «Не отправлено» значит утверждать факт, которого не было
        const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;
        const kind = code === 'ALREADY_CLAIMED' ? 'already' : 'failed';
        setAcceptError({
          proposalId,
          kind,
          message:
            kind === 'already'
              ? 'Черновик уже обработан — ответ отправлен'
              : getErrorMessage(error),
        });
      },
    });
  }, [acceptMutate]);

  const onReject = useCallback((proposalId: string) => {
    rejectMutate({ proposalId });
  }, [rejectMutate]);

  /**
   * Правка черновика с отчётом об отказе.
   *
   * До 08.09 отказ не показывался никак: у мутации не было `onError`, а
   * редактор закрывался безусловно. Маршрут отвечает 409, когда черновик
   * успели принять или отклонить, пока текст правили (авто-отправка,
   * вторая вкладка, коллега) — оператор видел закрытый редактор и прежний
   * текст, то есть ровно то же, что при успехе.
   *
   * Возвращает признак успеха: редактор закрывает вызывающий, и закрывать
   * его до подтверждения нельзя — правка потеряется молча.
   */
  const onEdit = useCallback(
    (proposalId: string, text: string): Promise<boolean> => {
      setAcceptError(null);
      return new Promise<boolean>((resolve) => {
        editMutate(
          { proposalId, text },
          {
            onSuccess: () => resolve(true),
            onError: (error) => {
              const code = (error as { response?: { data?: { code?: string } } }).response?.data
                ?.code;
              setAcceptError({
                proposalId,
                // Тот же словарь, что у принятия: `already` означает «ответ
                // как раз ушёл», и писать «не отправлено» было бы неправдой
                /*
                 * `failed` у принятия печатает «Не отправлено: …» — про
                 * судьбу ответа клиенту. У правки это утверждение о факте,
                 * которого не было: отправить никто и не пытался. Поэтому
                 * отказ правки всегда идёт как `already`: этот вид рисуется
                 * нейтрально, без слова «отправлено» (найдено ревью 08.09).
                 */
                kind: 'already',
                message:
                  code === 'NOT_READY'
                    ? 'Черновик уже обработан — правка не применена'
                    : `Правка не сохранилась: ${getErrorMessage(error)}`,
              });
              resolve(false);
            },
          }
        );
      });
    },
    [editMutate]
  );

  // Ошибка привязана к диалогу, в котором произошла: без сброса она
  // всплывала снова при возврате к тому же черновику
  useEffect(() => {
    setAcceptError(null);
  }, [sourceKey]);

  return useMemo(
    () => ({ onAccept, onReject, onEdit, acceptError, isAccepting }),
    [onAccept, onReject, onEdit, acceptError, isAccepting]
  );
}

// ============================================================================
// DialogueWorkspace
// ============================================================================

export function DialogueWorkspace({ item }: { item: InboxItem }): JSX.Element {
  // Карточка — над диалогом и одна на три канала: оператор открывает переписку
  // и первым делом видит суть, ради этого она и нужна
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <ConversationSummary source={inboxIdToSource(item)} totalMessages={item.messageCount} />
      <div className="flex-1 flex overflow-hidden">
        <ChannelDialogue item={item} />
      </div>
    </div>
  );
}

/**
 * Пропсы режимов для шапки диалога — одним хуком на все три канала.
 *
 * Раньше каждый канал держал СВОЙ `useState(false)` под «Забрать»: кнопка
 * утверждала состояние, которого могло не быть — после перезагрузки забранный
 * чат снова предлагал «Забрать», а бейдж режима не получал значения вовсе.
 * Правда лежит в базе, и спрашивается она здесь один раз.
 */
function useDialogueControls(
  channel: InboxItem['channel'],
  chatId: string
): {
  autoReplyMode: 'shadow' | 'bot' | 'assistant' | undefined;
  generationMode: GenerationMode | undefined;
  hasOwnModes: boolean;
  generationIsOwn: boolean;
  isChatTakenOver: boolean;
  setGeneration: (mode: GenerationMode | null) => void;
  takeover: () => void;
  returnToAI: () => void;
  error: string | null;
} {
  const { data } = useDialogueModes(channel, chatId);
  const setChatMode = useSetChatMode();

  return {
    autoReplyMode: data?.mode,
    generationMode: data?.generationMode,
    hasOwnModes: data?.modeSource === 'chat' || data?.generationSource === 'chat',
    // Только ось генерации: у забранного диалога общий признак истинен, а
    // генерация наследуется — переключатель обязан это показывать
    generationIsOwn: data?.generationSource === 'chat',
    // «Забрал» — это переопределение ДОСТАВКИ на shadow именно у этого
    // диалога. Унаследованный shadow значит «так настроена организация», и
    // предлагать «Вернуть AI» там нечего
    isChatTakenOver: data?.mode === 'shadow' && data.modeSource === 'chat',
    setGeneration: (mode) => setChatMode.mutate({ chatId, generationMode: mode }),
    takeover: () => setChatMode.mutate({ chatId, mode: 'shadow' }),
    /*
     * Снимается ТОЛЬКО ось доставки, а не переопределение целиком.
     *
     * `DELETE` убирает обе оси, и до этого шага терять было нечего — своей
     * оси генерации у диалога не существовало. Теперь администратор,
     * поставивший диалогу «черновики только по кнопке» и нажавший «Вернуть
     * AI», молча потерял бы эту настройку: кнопка сделала бы больше, чем
     * обещает. Отметил архитектурный проход 10.09.
     */
    returnToAI: () => setChatMode.mutate({ chatId, mode: null }),
    /*
     * Отказ обязан быть виден. Оператору доступна ось генерации, но НЕ ось
     * доставки: «Забрать» и «Вернуть AI» ответят ему 403, а у мутаций не было
     * `onError` — то есть человек нажимал, и не происходило ничего. Тот же
     * дефект уже находили 06.09 на принятии черновика.
     */
    error: setChatMode.error ? getErrorMessage(setChatMode.error) : null,
  };
}

function ChannelDialogue({ item }: { item: InboxItem }): JSX.Element {
  if (item.channel === 'email') {
    return <EmailDialogue threadId={item.id} />;
  }
  if (item.channel === 'widget') {
    return <WidgetDialogue sessionId={inboxIdToSource(item).sourceId} />;
  }
  return <TelegramDialogue chatId={Number(inboxIdToSource(item).sourceId)} />;
}

// ============================================================================
// Email
// ============================================================================

function EmailDialogue({ threadId }: { threadId: string }): JSX.Element {
  const { data: thread, isLoading, error } = useThread(threadId);
  const { data: rawProposals = [] } = useDialogueProposals('email', threadId);
  const { mutate: sendReply, isPending, error: sendError } = useSendReply();
  const { mutate: retryMessage } = useRetryMessage();
  const reviewActions = useReviewActions(`email:${threadId}`);
  const promote = usePromoteKnowledge();
  const checkpoint = useCreateCheckpoint();
  const prepareReply = useGenerateProposal();
  const режимы = useDialogueControls('email', threadId);

  const messages: Message[] = (thread?.messages ?? []).map(m => ({
    id: m.id,
    text: m.bodyText ?? '',
    isOutbound: m.direction === 'outbound',
    senderName: m.from,
    senderRole: m.direction === 'inbound' ? 'client' : 'operator',
    timestamp: m.sentAt,
    ...(m.sendStatus ? { sendStatus: m.sendStatus } : {}),
    ...(m.sendError ? { sendError: m.sendError } : {}),
  }));

  const proposals: Proposal[] = rawProposals.map(элементВЧерновик);

  const handleSend = useCallback((text: string) => {
    sendReply({ threadId, text, sendRequestId: crypto.randomUUID() });
  }, [sendReply, threadId]);

  return (
    <>
      <ShadowPanelView
        messages={messages}
        proposals={proposals}
        isLoading={isLoading}
        error={error}
        reviewActions={reviewActions}
      />
      <RealPanelView
        messages={messages}
        proposals={proposals}
        isLoading={isLoading}
        error={error}
        onSend={handleSend}
        isPending={isPending}
        sendError={sendError}
        onRetryMessage={retryMessage}
        threadId={threadId}
        onPromoteKnowledge={(title, content) => promote.mutate({ title, content })}
        onCheckpoint={(type) => checkpoint.mutate({ threadId, type })}
        onTakeoverChat={режимы.takeover}
        onReturnToAI={режимы.returnToAI}
        isChatTakenOver={режимы.isChatTakenOver}
        modeError={режимы.error}
        autoReplyMode={режимы.autoReplyMode}
        generationMode={режимы.generationMode}
        hasOwnModes={режимы.hasOwnModes}
        generationIsOwn={режимы.generationIsOwn}
        onGenerationModeChange={режимы.setGeneration}
        onPrepareReply={() => prepareReply.mutate({ sourceType: 'email', sourceId: threadId })}
        isPreparingReply={prepareReply.isPending}
        prepareReplyError={ошибкаПодготовки(prepareReply.error)}
        onCorrectMessage={(_msgId, correctedText) => {
          // Находим вопрос клиента перед этим AI-ответом
          const msgs = messages;
          const aiMsgIdx = msgs.findIndex(m => m.id === _msgId);
          const clientMsg = aiMsgIdx > 0 ? msgs.slice(0, aiMsgIdx).reverse().find(m => !m.isOutbound) : null;
          const title = clientMsg?.text ?? 'Вопрос клиента';
          promote.mutate({ title, content: correctedText });
        }}
      />
    </>
  );
}

// ============================================================================
// Telegram
// ============================================================================

function TelegramDialogue({ chatId }: { chatId: number }): JSX.Element {
  const { data: rawMessages = [], isLoading, error } = useTelegramMessages(chatId);
  const { data: rawProposals = [] } = useTelegramProposals(chatId);
  const { mutate: sendMessage, isPending, error: sendError } = useSendTelegramMessage(chatId);
  const reviewActions = useReviewActions(`telegram:${chatId}`);
  const promote = usePromoteKnowledge();
  const checkpoint = useCreateCheckpoint();
  const prepareReply = useGenerateProposal();
  const chatIdStr = String(chatId);
  const режимы = useDialogueControls('telegram', chatIdStr);

  const messages = rawMessages.map(telegramMsgToMessage);
  const proposals = rawProposals.map(telegramProposalToProposal);

  const handleSend = useCallback((text: string) => {
    sendMessage(text);
  }, [sendMessage]);

  return (
    <>
      <ShadowPanelView
        messages={messages}
        proposals={proposals}
        isLoading={isLoading}
        error={error}
        reviewActions={reviewActions}
      />
      <RealPanelView
        messages={messages}
        proposals={proposals}
        isLoading={isLoading}
        error={error}
        onSend={handleSend}
        isPending={isPending}
        sendError={sendError}
        threadId={chatIdStr}
        onPromoteKnowledge={(title, content) => promote.mutate({ title, content })}
        onCheckpoint={(type) => checkpoint.mutate({ threadId: chatIdStr, type })}
        onPrepareReply={() => prepareReply.mutate({ sourceType: 'telegram', sourceId: chatIdStr })}
        isPreparingReply={prepareReply.isPending}
        prepareReplyError={ошибкаПодготовки(prepareReply.error)}
        onTakeoverChat={режимы.takeover}
        onReturnToAI={режимы.returnToAI}
        isChatTakenOver={режимы.isChatTakenOver}
        modeError={режимы.error}
        autoReplyMode={режимы.autoReplyMode}
        generationMode={режимы.generationMode}
        hasOwnModes={режимы.hasOwnModes}
        generationIsOwn={режимы.generationIsOwn}
        onGenerationModeChange={режимы.setGeneration}
        onCorrectMessage={(_msgId, correctedText) => {
          // Находим вопрос клиента перед этим AI-ответом
          const msgs = messages;
          const aiMsgIdx = msgs.findIndex(m => m.id === _msgId);
          const clientMsg = aiMsgIdx > 0 ? msgs.slice(0, aiMsgIdx).reverse().find(m => !m.isOutbound) : null;
          const title = clientMsg?.text ?? 'Вопрос клиента';
          promote.mutate({ title, content: correctedText });
        }}
      />
    </>
  );
}

// ============================================================================
// Widget
// ============================================================================

function widgetMsgToMessage(msg: WidgetMessage): Message {
  return {
    id: msg.id,
    text: msg.text,
    isOutbound: msg.actorRole !== 'client',
    senderName: msg.actorRole === 'client' ? 'Посетитель' : msg.actorRole === 'ai' ? 'Anima' : 'Оператор',
    senderRole: msg.actorRole === 'client' ? 'client' : 'operator',
    ...(msg.actorRole === 'ai' ? { sentBy: 'ai' as const } : {}),
    timestamp: msg.createdAt,
  };
}

function WidgetDialogue({ sessionId }: { sessionId: string }): JSX.Element {
  const { data: rawMessages = [], isLoading, error } = useWidgetMessages(sessionId);
  const { data: rawProposals = [] } = useDialogueProposals('widget', sessionId);
  const reviewActions = useReviewActions(`widget:${sessionId}`);
  const sendReply = useSendWidgetReply(sessionId);
  const promote = usePromoteKnowledge();
  const checkpoint = useCreateCheckpoint();
  const prepareReply = useGenerateProposal();
  const режимы = useDialogueControls('widget', sessionId);

  const messages = rawMessages.map(widgetMsgToMessage);

  const proposals: Proposal[] = rawProposals.map(элементВЧерновик);

  const handleSend = (text: string) => {
    sendReply.mutate(text);
  };

  /*
   * Повтор не ушёл клиенту — и оператор должен узнать это словами. 10.09 два
   * нажатия «Отправить» ушли клиенту дважды одним текстом; теперь второй раз
   * не уходит, но тихо проглотить было бы хуже дубля.
   */
  const повторОтвета = sendReply.data?.duplicate
    ? (sendReply.data.message ?? 'Такой ответ уже отправлен — второй раз клиенту не ушёл')
    : null;

  return (
    <>
      <ShadowPanelView
        messages={messages}
        proposals={proposals}
        isLoading={isLoading}
        error={error}
        reviewActions={reviewActions}
      />
      <RealPanelView
        messages={messages}
        proposals={proposals}
        isLoading={isLoading}
        error={error}
        onSend={handleSend}
        isPending={sendReply.isPending}
        sendError={sendReply.error}
        sendNotice={повторОтвета}
        threadId={sessionId}
        onPromoteKnowledge={(title, content) => promote.mutate({ title, content })}
        onCheckpoint={(type) => checkpoint.mutate({ threadId: sessionId, type })}
        onPrepareReply={() => prepareReply.mutate({ sourceType: 'widget', sourceId: sessionId })}
        isPreparingReply={prepareReply.isPending}
        prepareReplyError={ошибкаПодготовки(prepareReply.error)}
        onTakeoverChat={режимы.takeover}
        onReturnToAI={режимы.returnToAI}
        isChatTakenOver={режимы.isChatTakenOver}
        modeError={режимы.error}
        autoReplyMode={режимы.autoReplyMode}
        generationMode={режимы.generationMode}
        hasOwnModes={режимы.hasOwnModes}
        generationIsOwn={режимы.generationIsOwn}
        onGenerationModeChange={режимы.setGeneration}
        onCorrectMessage={(_msgId, correctedText) => {
          // Находим вопрос клиента перед этим AI-ответом
          const msgs = messages;
          const aiMsgIdx = msgs.findIndex(m => m.id === _msgId);
          const clientMsg = aiMsgIdx > 0 ? msgs.slice(0, aiMsgIdx).reverse().find(m => !m.isOutbound) : null;
          const title = clientMsg?.text ?? 'Вопрос клиента';
          promote.mutate({ title, content: correctedText });
        }}
      />
    </>
  );
}

// ============================================================================
// Empty state
// ============================================================================

export function EmptyWorkspace(): JSX.Element {
  return (
    <div className="flex-1 flex items-center justify-center text-text-secondary">
      <div className="text-center max-w-xs">
        <div className="w-12 h-12 rounded-comp bg-accent-ai/10 border border-accent-ai/20 flex items-center justify-center mx-auto mb-4">
          <span className="text-accent-ai text-xl">A</span>
        </div>
        <p className="font-heading font-medium text-lg text-text-primary">Выберите диалог</p>
        <p className="text-sm mt-1">Anima покажет свой вариант ответа рядом с реальным</p>
        <p className="text-xs mt-4 text-text-secondary/70 leading-relaxed">
          Anima учится на каждом вашем решении — принять, отредактировать или отклонить.
          Чем больше ответов вы обработаете, тем точнее станут черновики.
        </p>
      </div>
    </div>
  );
}
